import { Badge, DateInput, type BadgeTone } from '@safetyhub/ui';
import {
  EQUIPMENT_REGIME_LABEL,
  EQUIPMENT_REGIMES,
  judgeRegulatedEquipment,
  withObjectParticle,
  type EquipmentRegime,
  type EquipmentSpecField,
  type EquipmentSpecValues,
  type RegulatedEquipmentJudgement,
} from '@safetyhub/shared';
import type { RegulatedEquipmentClassView } from '../../../../lib/regulatedEquipment';

/**
 * 법정 설비 칸(②-c2 E1 — 설계 2차 `docs/plans/2026-11-02-legal-obligation-subject-facts-design.md` §2-2 「화면 표시」).
 *
 * ## 사람은 «사실»을 넣고 판정은 시스템이 한다(사용자 결정 E1)
 * 이 화면에는 「법정 대상이다/아니다」를 고르는 칸이 없다 — 기종(무슨 기계인가)·규격 값(명판의 수)·형식·용도(체크)만 받고, 배지는
 * shared `judgeRegulatedEquipment`(서버 조건 판정과 **같은 함수**)가 그린다. 이름(대상명)은 판정에 쓰지 않는다 — 「크레인」이라는 이름이어도
 * 기종을 고르기 전에는 판정이 없다.
 *
 * ## 미입력과 「해당 사항 없음」은 다르다
 * 다중 선택(형식·용도)을 하나도 안 고른 상태는 «미입력»(판정 «확인 필요»)이고, 「해당 사항 없음」 체크가 빈 목록(응답)이다. 예/아니오도
 * 셋(미입력·예·아니오)이다 — 기본값을 「아니오」로 두면 모름이 「해당 없음」으로 접힌다.
 */

const STATUS_TONE: Record<RegulatedEquipmentJudgement['status'], BadgeTone> = {
  REGULATED: 'blue',
  NOT_REGULATED: 'slate',
  UNKNOWN: 'amber',
};

function toValues(raw: Record<string, unknown> | null | undefined): EquipmentSpecValues {
  return (raw ?? {}) as EquipmentSpecValues;
}

function labelsOf(fields: readonly EquipmentSpecField[], keys: readonly string[]): string {
  const byKey = new Map(fields.map((f) => [f.key, f.label]));
  return keys.map((k) => byKey.get(k) ?? k).join(' · ');
}

/**
 * 다른 사실 축이 채우는 값(`source` 필드 — 국소배기의 「최근 2년 노출기준 50% 미만」)과 그 사유. 설비 목록 응답의 `externalFacts`·
 * `externalFactNotes`를 그대로 넘긴다 — 판정 함수는 서버와 같고 **입력도 같아야** 같은 판정이 나온다(없으면 그 필드는 늘 미입력 = UNKNOWN).
 */
export interface ExternalFactsInput {
  facts?: Record<string, unknown> | null;
  notes?: readonly string[];
}

/** 한 제도의 판정 → 화면 문장(설계 2차 §2-2 「화면 표시」 표) */
export function describeRegime(
  cls: RegulatedEquipmentClassView,
  regime: EquipmentRegime,
  values: Record<string, unknown> | null | undefined,
  at: Date = new Date(),
  external?: ExternalFactsInput,
): { tone: BadgeTone; label: string; detail: string | null; judgement: RegulatedEquipmentJudgement } | null {
  const judgement = judgeRegulatedEquipment(cls, regime, toValues(values), { at, externalFacts: toValues(external?.facts) });
  if (judgement.regimeUndefined) return null;
  const name = EQUIPMENT_REGIME_LABEL[regime];
  if (judgement.notYetEffective) {
    return { tone: 'slate', label: `${name} 대상 아님(시행 전)`, detail: cls.regimes[regime]?.effectiveFrom ?? null, judgement };
  }
  if (judgement.status === 'UNKNOWN') {
    if (judgement.rulePending) return { tone: 'amber', label: `${name} 규칙 확인 중`, detail: null, judgement };
    // 사람이 넣는 칸과 다른 축이 채우는 칸(`source`)을 갈라 말한다 — 후자는 「넣으면」이 아니라 그 축의 사유(배기 공정·측정 결과)다
    const sourceKeys = new Set(cls.specFields.filter((f) => f.source).map((f) => f.key));
    const human = judgement.missingFields.filter((k) => !sourceKeys.has(k));
    const fromSource = judgement.missingFields.filter((k) => sourceKeys.has(k));
    const parts = [
      // 조사는 마지막 항목 이름의 받침으로 고른다(「해당하는 형식·용도를」 · dev 실측 2026-11-02 「…용도을(를)」) — 판정 불가면 「을(를)」
      human.length > 0 ? `${withObjectParticle(labelsOf(cls.specFields, human))} 넣으면 판정됩니다` : null,
      fromSource.length > 0
        ? external?.notes && external.notes.length > 0
          ? external.notes.join(' · ')
          : `${labelsOf(cls.specFields, fromSource)} — 측정 결과에서 연결됩니다`
        : null,
    ].filter((p): p is string => p !== null);
    return { tone: STATUS_TONE.UNKNOWN, label: `${name} 확인 필요`, detail: parts.length > 0 ? parts.join(' · ') : null, judgement };
  }
  if (judgement.status === 'NOT_REGULATED') {
    return { tone: STATUS_TONE.NOT_REGULATED, label: `${name} 대상 아님`, detail: judgement.reasons.join(' · ') || null, judgement };
  }
  const routing =
    judgement.routingMissingFields.length > 0 ? `주기 확인 필요(${labelsOf(cls.specFields, judgement.routingMissingFields)})` : null;
  return { tone: STATUS_TONE.REGULATED, label: `${name} 대상`, detail: routing ?? (judgement.reasons.join(' · ') || null), judgement };
}

/** 목록·상세의 배지 줄 — 그 기종에 규칙이 있는 제도만 */
export function RegulatedEquipmentBadges({
  cls,
  values,
  external,
  showDetail = false,
}: {
  cls: RegulatedEquipmentClassView | undefined;
  values: Record<string, unknown> | null | undefined;
  /** 설비 응답의 `externalFacts`·`externalFactNotes`(목록·상세) — 폼 미리보기는 저장된 설비의 것을 넘긴다 */
  external?: ExternalFactsInput;
  showDetail?: boolean;
}) {
  if (!cls) return null;
  const rows = EQUIPMENT_REGIMES.map((r) => describeRegime(cls, r, values, new Date(), external)).filter((r): r is NonNullable<typeof r> => r !== null);
  if (rows.length === 0) return null;
  return (
    <div className={showDetail ? 'space-y-1.5' : 'flex flex-wrap items-center gap-1'} data-testid="regulated-equipment-badges">
      {rows.map((r) => (
        <div key={r.label} className={showDetail ? '' : 'inline-flex'}>
          <Badge tone={r.tone} shape="rounded">
            {r.label}
          </Badge>
          {showDetail && r.detail && <p className="mt-0.5 text-xs text-ink-500">{r.detail}</p>}
        </div>
      ))}
    </div>
  );
}

const inputClass = 'w-full px-3 py-2 border border-line rounded-control text-sm focus:ring-brand-500 focus:border-brand-500';

function NumberField({ field, value, onChange }: { field: EquipmentSpecField; value: unknown; onChange: (v: number | undefined) => void }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink-700 mb-1">
        {field.label}
        {field.unit ? <span className="text-ink-400"> ({field.unit})</span> : null}
      </span>
      <input
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        value={typeof value === 'number' ? String(value) : ''}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') return onChange(undefined);
          const n = Number(raw);
          onChange(Number.isFinite(n) ? n : undefined);
        }}
        className={inputClass}
      />
      {field.help && <span className="mt-0.5 block text-xs text-ink-400">{field.help}</span>}
    </label>
  );
}

function BooleanField({ field, value, onChange }: { field: EquipmentSpecField; value: unknown; onChange: (v: boolean | undefined) => void }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink-700 mb-1">{field.label}</span>
      <select
        value={value === true ? 'yes' : value === false ? 'no' : ''}
        onChange={(e) => onChange(e.target.value === 'yes' ? true : e.target.value === 'no' ? false : undefined)}
        className={inputClass}
      >
        <option value="">미입력</option>
        <option value="yes">예</option>
        <option value="no">아니오</option>
      </select>
    </label>
  );
}

function EnumField({ field, value, onChange }: { field: EquipmentSpecField; value: unknown; onChange: (v: string | undefined) => void }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink-700 mb-1">{field.label}</span>
      <select value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value || undefined)} className={inputClass}>
        <option value="">미입력</option>
        {(field.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function EnumSetField({ field, value, onChange }: { field: EquipmentSpecField; value: unknown; onChange: (v: string[] | undefined) => void }) {
  const selected = Array.isArray(value) ? (value as string[]) : null;
  const noneChecked = selected !== null && selected.length === 0;
  const toggle = (v: string) => {
    const base = selected ?? [];
    const next = base.includes(v) ? base.filter((x) => x !== v) : [...base, v];
    // 마지막 선택을 풀면 «미입력»으로 돌아간다 — 「해당 사항 없음」은 그 체크로만 응답한다
    onChange(next.length > 0 ? next : undefined);
  };
  return (
    <fieldset>
      <legend className="block text-sm font-medium text-ink-700 mb-1">{field.label}</legend>
      {field.help && <p className="mb-1 text-xs text-ink-400">{field.help}</p>}
      <div className="max-h-48 overflow-y-auto rounded-control border border-line p-2 space-y-1">
        <label className="flex items-start gap-2 text-sm text-ink-900">
          <input type="checkbox" checked={noneChecked} onChange={() => onChange(noneChecked ? undefined : [])} className="mt-0.5" />
          <span>해당 사항 없음</span>
        </label>
        {(field.options ?? []).map((o) => (
          <label key={o.value} className="flex items-start gap-2 text-sm text-ink-700">
            <input type="checkbox" checked={!!selected?.includes(o.value)} onChange={() => toggle(o.value)} className="mt-0.5" />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** 배기하는 측정 공정 고르기(국소배기장치 — 측정 결과 축 필드가 있는 기종만) — 선택지는 현장의 활성 측정 공정 */
function ExhaustProcessField({
  processes,
  selected,
  onChange,
}: {
  processes: readonly { id: string; name: string }[];
  selected: readonly string[];
  onChange: (ids: string[]) => void;
}) {
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <fieldset data-testid="exhaust-process-field">
      <legend className="block text-sm font-medium text-ink-700 mb-1">배기하는 측정 공정</legend>
      <p className="mb-1 text-xs text-ink-400">
        이 장치가 배기하는 공정을 고르세요. 그 공정의 작업환경측정 결과(최근 2년)로 「노출기준 50% 미만 제외」가 판정됩니다.
      </p>
      {processes.length === 0 ? (
        <p className="text-xs text-ink-500">등록된 측정 공정이 없습니다 — 법정 서류의 「작업환경측정 대상」 명부에서 공정을 먼저 등록하세요.</p>
      ) : (
        <div className="max-h-40 overflow-y-auto rounded-control border border-line p-2 space-y-1">
          {processes.map((p) => (
            <label key={p.id} className="flex items-start gap-2 text-sm text-ink-700">
              <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} className="mt-0.5" />
              <span>{p.name}</span>
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
}

/**
 * 추가/수정 모달의 법정 칸 — 기종 선택 · 설치일 · 그 기종의 규격 칸(`specFields` 중 사람이 넣는 것만) · 배기 공정(측정 결과 축 기종만) ·
 * 배지 미리보기. 기종을 바꾸면 규격 값·배기 공정을 비운다(서버도 같은 처분 — 다른 기종의 값이 같은 키로 남지 않게).
 */
export function RegulatedEquipmentFields({
  classes,
  kindCode,
  specValues,
  installedDate,
  processes = [],
  exhaustProcessIds = [],
  external,
  onKindChange,
  onSpecChange,
  onInstalledDateChange,
  onExhaustProcessChange,
}: {
  classes: readonly RegulatedEquipmentClassView[];
  kindCode: string;
  specValues: Record<string, unknown>;
  /** `YYYY-MM-DD`(현장 시간대 날짜) · 빈 문자열 = 미입력 */
  installedDate: string;
  /** 배기 공정 선택지(현장 활성 측정 공정 — `SiteEquipmentLegalContext.processes`) */
  processes?: readonly { id: string; name: string }[];
  exhaustProcessIds?: readonly string[];
  /** 수정 모드에서 저장된 설비의 측정 결과 사실(미리보기 배지용 — 폼의 배기 공정 변경은 저장 뒤에 반영된다) */
  external?: ExternalFactsInput;
  onKindChange: (code: string) => void;
  onSpecChange: (values: Record<string, unknown>) => void;
  onInstalledDateChange: (date: string) => void;
  onExhaustProcessChange?: (ids: string[]) => void;
}) {
  const cls = classes.find((c) => c.code === kindCode);
  const hasMeasurementSource = !!cls?.specFields.some((f) => f.source === 'WORK_ENVIRONMENT_MEASUREMENT');
  const setValue = (key: string, value: unknown) => {
    const next = { ...specValues };
    if (value === undefined) delete next[key];
    else next[key] = value;
    onSpecChange(next);
  };
  const unitNotes = cls ? EQUIPMENT_REGIMES.map((r) => cls.regimes[r]?.unitNote).filter((n): n is string => !!n) : [];

  return (
    <div className="space-y-3 rounded-card border border-line p-3" data-testid="regulated-equipment-fields">
      <div>
        <p className="text-sm font-medium text-ink-900">법정 설비 분류</p>
        <p className="text-xs text-ink-400">기종과 명판의 규격 값을 넣으면 안전검사·자율안전확인 대상인지 판정됩니다(대상명은 판정에 쓰지 않습니다).</p>
      </div>
      <label className="block">
        <span className="block text-sm font-medium text-ink-700 mb-1">기종</span>
        <select value={kindCode} onChange={(e) => onKindChange(e.target.value)} className={inputClass} aria-label="기종">
          <option value="">선택 안 함(법정 설비 아님 · 장소 등)</option>
          {classes.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      {cls && (
        <>
          {unitNotes.map((n) => (
            <p key={n} className="text-xs text-ink-500">
              {n}
            </p>
          ))}
          {/* 날짜만 받는 칸 — 값은 `YYYY-MM-DD`(현장 기본 시간대 날짜)이고 UTC 변환(로컬 00:00 → UTC)은 저장하는 쪽
              (`SiteEquipmentSection`의 `parseDateLocalToUTC`)이 한다 */}
          <div>
            <span className="block text-sm font-medium text-ink-700 mb-1">설치일(이동식은 신규등록일)</span>
            <DateInput
              label="설치일"
              value={installedDate}
              onChange={onInstalledDateChange}
              placeholder="날짜 선택"
              className="w-full"
            />
          </div>
          {hasMeasurementSource && onExhaustProcessChange && (
            <ExhaustProcessField processes={processes} selected={exhaustProcessIds} onChange={onExhaustProcessChange} />
          )}
          {cls.specFields.map((f) => {
            if (f.source) {
              return (
                <p key={f.key} className="text-xs text-ink-500">
                  {f.label} — {f.help ?? '다른 기록에서 연결됩니다'}
                </p>
              );
            }
            const value = specValues[f.key];
            switch (f.type) {
              case 'number':
                return <NumberField key={f.key} field={f} value={value} onChange={(v) => setValue(f.key, v)} />;
              case 'boolean':
                return <BooleanField key={f.key} field={f} value={value} onChange={(v) => setValue(f.key, v)} />;
              case 'enum':
                return <EnumField key={f.key} field={f} value={value} onChange={(v) => setValue(f.key, v)} />;
              case 'enumSet':
                return <EnumSetField key={f.key} field={f} value={value} onChange={(v) => setValue(f.key, v)} />;
              default:
                return null;
            }
          })}
          <RegulatedEquipmentBadges cls={cls} values={specValues} external={external} showDetail />
        </>
      )}
      {!cls && <p className="text-xs text-ink-500">법정 안전검사 대상인지 보려면 기종을 고르세요.</p>}
    </div>
  );
}
